import { useCallback, useRef, useState } from 'react'

/**
 * Generic hook for managing multiple modal states
 * Replaces individual boolean states for each modal
 *
 * @example
 * const modals = useModalManager(['newPlaybook', 'settings', 'share'])
 * modals.open('newPlaybook')
 * modals.close('settings')
 * modals.isOpen('share') // false
 */
export function useModalManager<T extends string>(modalNames: readonly T[]) {
	const [openModals, setOpenModals] = useState<Set<T>>(new Set())
	const openModalsRef = useRef(openModals)
	openModalsRef.current = openModals

	const open = useCallback((name: T) => {
		setOpenModals(prev => new Set(prev).add(name))
	}, [])

	const close = useCallback((name: T) => {
		setOpenModals(prev => {
			const next = new Set(prev)
			next.delete(name)
			return next
		})
	}, [])

	const toggle = useCallback((name: T) => {
		setOpenModals(prev => {
			const next = new Set(prev)
			if (next.has(name)) {
				next.delete(name)
			} else {
				next.add(name)
			}
			return next
		})
	}, [])

	// Use ref pattern to avoid stale closure - isOpen callback is stable
	// and always reads current state from the ref
	const isOpen = useCallback((name: T) => openModalsRef.current.has(name), [])

	const closeAll = useCallback(() => {
		setOpenModals(new Set())
	}, [])

	return { open, close, toggle, isOpen, closeAll }
}
